import moment from 'moment';

const veeConfig = {
  inject: false,
  fieldsBagName: 'veeFields',
  events: '',
  /**
   * An input field's default error messages will be overwritten if 1) the input field's
   * `field_name` has a record in this dictionary, and 2) that input field's invalidated
   * rule exists within that record.
   *
   * However, this dictionary does not establish a parity with the API since the API can
   * change both its `field_name` values and its rules.
   */
  dictionary: {
    en: {
      custom: {
        dob: {
          date_format: 'The date of birth must be in the format MM/DD/YYYY.',
        },
        email: {
          email: 'Email Address must be valid.',
        },
        zipcode: {
          digits: 'Zip Code must contain 5 digits.',
        },
        fname: {
          regex: 'Invalid Character(s)',
        },
        lname: {
          regex: 'Invalid Character(s)',
        },
        username: {
          email: 'Username must be valid.',
        },
        password: {
        },
      },
      messages: {
        has_time_between: () => {
          return `The selected start date indicates a policy lapse. Please change the policy start date before continuing.`;
        },
        has_compare_integers: () => {
          return `Continuously insured cannot be less than Years with recent company.`;
        },
        has_alpha_uppercase: () => {
          return `At Least 1 Uppercase Character (A-Z)`;
        },
        has_alpha_lowercase: () => {
          return `At Least 1 Lowercase Character (a-z)`;
        },
        has_met_password_standards: () => {
          return `Password must meet the requirements.`;
        },
        has_numeric: () => {
          return `At Least 1 Digit (0-9)`;
        },
        has_special_char: () => {
          return `At Least 1 Special Character (!"#$%&'()*+,-./:;<=>?@[]^_\`{|}~)`;
        },
        confirmed_match: () => {
          return `The fields do not match.`;
        },
        isEqualTo: () => {
          return `Password and confirm password do not match.`;
        },
        within_year_range: (fieldName, params) => {
          let min; // Max years ago, which is the minimum date
          let max; // Min years ago, which is the maximum date
          let format; // Built-in rule 'date_between' requires the date format
          let minDate;
          let maxDate;
          /* eslint prefer-const: */
          [min, max, format] = params;

          minDate = moment().subtract(min, 'years').format(format);
          maxDate = moment().subtract(max, 'years').format(format);

          return `The date must be between ${minDate} and ${maxDate}.`;
        },
        within_custom_date_range: (fieldName, params) => {
          let min; // Minimum magnitude (used in conjunction with time_unit_min)
          let max; // Maximum magnitude (used in conjunction with time_unit_max)
          let timeUnitMin; // Unit of time (days, weeks, months, years)
          let timeUnitMax; // Unit of time (days, weeks, months, years)
          let format; // Built-in rule 'date_between' requires the date format
          let minDate;
          let maxDate;
          [min, max, timeUnitMin, timeUnitMax, format] = params;
          minDate = moment().subtract(min, timeUnitMin).format(format);
          maxDate = moment().add(max, timeUnitMax).format(format);
          return `The date must be between ${minDate} and ${maxDate}.`;
        },
        within_custom_date_range_reports: () => {
          return `Invalid Date.`;
        },
        within_custom_date_range_exp: (fieldName, params) => {
          let min; // Minimum magnitude (used in conjunction with time_unit_min)
          let max; // Maximum magnitude (used in conjunction with time_unit_max)
          let timeUnitMin; // Unit of time (days, weeks, months, years)
          let timeUnitMax; // Unit of time (days, weeks, months, years)
          let format; // Built-in rule 'date_between' requires the date format
          let minDate;
          let maxDate;
          [min, max, timeUnitMin, timeUnitMax, format] = params;
          minDate = moment().subtract(min, timeUnitMin).format(format);
          maxDate = moment().add(max, timeUnitMax).format(format);
          return `The date must be between ${minDate} and ${maxDate}.`;
        },
        within_year_date_range: (fieldName, params) => {
          let min; // Minimum date
          let max; // Maximum date, or if less than min year then # of years into the future
          let format; // Built-in rule 'date_between' requires the date format
          let minDate;
          let maxDate;
          [min, max, format] = params;
          minDate = min; // Simply use the min value as the minimum year
          /**
           * In the case that the max year is less than the min year,
           * use the value of 'max' as the number of years from present
           * into the future.
           */
          if (max < min) {
            maxDate = moment().add(max, 'years').format(format);
          } else {
            maxDate = max; // Simply use the max value as the maximum year
          }
          return `The date must be between ${minDate} and ${maxDate}.`;
        },
        /* eslint arrow-body-style: */
        date_format: (fieldName, dateFormat) => {
          return `You can only enter a date in the format "${dateFormat[0]}".`;
        },
        required: (fieldName) => {
          const fieldNameArr = fieldName.split('_');
          if (fieldNameArr[0] && fieldNameArr[0] === 'firstName') {
            return `First name is required.`;
          }
          if (fieldNameArr[0] && fieldNameArr[0] === 'lastName') {
            return `Last name is required.`;
          }
          if (fieldNameArr[0] && fieldNameArr[0] === 'email') {
            return `Email is required.`;
          }
          if (fieldNameArr[0] && fieldNameArr[0] === 'role') {
            return `Role is required.`;
          }
          return 'Required.';
        },
        required_checkbox: () => {
          return 'This box must be checked to proceed.';
        },
        email: () => {
          return 'Invalid Email Address';
        },
        min_value: (fieldName, lengthAmount) => {
          if (fieldName === 'carrier_timeout') {
            return `Value cannot be zero.`;
          }
          return `You can only enter a minimum value of ${lengthAmount[0]}.`;
        },
        max_value: (fieldName, lengthAmount) => {
          if (fieldName === 'carrier_timeout') {
            return `Maximum timeout is ${lengthAmount[0]} seconds.`;
          }
          return `You can only enter a maximum value of ${lengthAmount[0]}.`;
        },
        length: (fieldName, lengthAmount) => {
          return `You can only use exactly ${lengthAmount[0]} characters.`;
        },
        digits: (fieldName, lengthAmount) => {
          const fieldNameArr = fieldName.split('_');
          if (fieldNameArr[0] && fieldNameArr[0] === 'phoneNumber') {
            return `Phone number must be exactly ${lengthAmount[0]} digits.`;
          }
          return `You can only use exactly ${lengthAmount[0]} digits.`;
        },
        min: (fieldName, lengthAmount) => {
          return `Min. Number of Characters: ${lengthAmount[0]}`;
        },
        max: (fieldName, lengthAmount) => {
          return `You can only enter at most ${lengthAmount[0]} characters.`;
        },
        numeric: () => {
          return `You can only enter numbers.`;
        },
        integer: () => {
          return `You can only enter numbers.`;
        },
        regex: (fieldName, customRegex) => {
          let customErrorMessage = 'This format is invalid.';
          /**
           * below logic is to add field name in error message for bulk user front end validation
           */
          const fieldNameArr = fieldName.split('_');
          if (fieldNameArr[0] && fieldNameArr[0] === 'firstName') {
            customErrorMessage = 'First name format is invalid.';
          }
          if (fieldNameArr[0] && fieldNameArr[0] === 'lastName') {
            customErrorMessage = 'Last name format is invalid.';
          }
          if (fieldNameArr[0] && fieldNameArr[0] === 'middleInitial') {
            customErrorMessage = 'Middle initial format is invalid.';
          }
          let customRegexFn = customRegex[0].toString();
          /* eslint quotes: */
          switch (customRegexFn) {
            // Proper name
            case `/^[a-zA-Z-' ]+$/`:
              customErrorMessage = `Only letters, hyphens, apostrophes and spaces are allowed.`;
              break;

            // Proper carriername
            case `/^[a-zA-Z0-9 ]+$/`:
              customErrorMessage = `Only letters, numbers and spaces are allowed.`;
              break;
            // Proper carriertag
            case `/^[a-zA-Z0-9]+$/`:
              customErrorMessage = `Only alphanumeric characters are allowed.`;
              break;

            // Proper VIN
            case "/^(?:(?![iIoOqQ])[a-zA-Z0-9-' ])+$/":
              customErrorMessage = `You can only use exactly 17 characters. You can only use letters
                 that are not "i,I,o,O,q,Q", and numbers for the VIN.`;
              break;

            // Yes/No
            case '/^[YN]$/':
              customErrorMessage = 'You can only use \'Y\' or \'N\'.';
              break;
            default:
              break;
          }

          return customErrorMessage;
        },
        alpha: () => { return 'You can only enter letters.'; },
        required_privacy_checkbox: () => {
          return `This box must be checked to proceed.`;
        },
      },
    },
  },
};

export default veeConfig;



// WEBPACK FOOTER //
// ./src/custom-error-messages.js